import React, { useMemo, useState } from 'react';
import { Calendar } from 'lucide-react';
import { MONTH_OPTIONS } from '../../constants/formOptions';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function buildYearOptions(span = 50) {
  const current = new Date().getFullYear();
  return Array.from({ length: span + 1 }, (_, i) => String(current - span + i));
}

function monthShort(month) {
  return MONTH_OPTIONS.find((m) => m.value === month)?.label.slice(0, 3) ?? 'Jan';
}

function formatDurationPart(part, precision) {
  if (precision === 'year') return part.year;
  return `${monthShort(part.month)} ${part.year}`;
}

function formatDurationRange(start, end, precision) {
  const left = formatDurationPart(start, precision);
  if (end === 'Present') return `${left} - Present`;
  return `${left} - ${formatDurationPart(end, precision)}`;
}

function parseDurationRange(value, precision) {
  const today = new Date();
  const fallback = {
    year: String(today.getFullYear()),
    month: pad2(today.getMonth() + 1),
  };
  const raw = (value || '').trim();
  if (!raw) {
    return { start: fallback, end: fallback, isPresent: false };
  }

  const isPresent = /\bpresent\b/i.test(raw);
  const cleaned = raw.replace(/\bpresent\b/i, '').replace(/\s*-\s*$/, '').trim();
  const sides = cleaned.split(/\s*[-–—]\s*/).filter(Boolean);

  const parseSide = (side) => {
    if (!side) return fallback;
    const yearMatch = side.match(/(19|20)\d{2}/);
    const year = yearMatch?.[0] ?? fallback.year;
    if (precision === 'year') return { year, month: '01' };

    const lower = side.toLowerCase();
    const month = MONTH_OPTIONS.find(
      (m) =>
        lower.includes(m.label.toLowerCase()) ||
        lower.includes(m.label.slice(0, 3).toLowerCase()),
    );
    const ym = side.match(/(19|20)\d{2}-(\d{2})/);
    return {
      year,
      month: ym?.[2] ?? month?.value ?? '01',
    };
  };

  return {
    start: parseSide(sides[0]),
    end: isPresent ? fallback : parseSide(sides[1] ?? sides[0]),
    isPresent,
  };
}

function ChipRow({ options, selected, onSelect, labelKey = 'label', valueKey = 'value' }) {
  return (
    <div className="flex flex-wrap gap-2 mb-3">
      {options.map((opt) => {
        const val = typeof opt === 'string' ? opt : opt[valueKey];
        const label = typeof opt === 'string' ? opt : opt[labelKey];
        const active = selected === val;
        return (
          <button
            key={val}
            type="button"
            onClick={() => onSelect(val)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
              active
                ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-[var(--color-text-inverse)]'
                : 'bg-[var(--color-surface-muted)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-subtle)]'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Duration range picker (matches mobile DurationRangeField).
 */
export default function DurationRangeField({
  value,
  onChange,
  placeholder,
  precision = 'month',
  allowPresent = false,
  title = 'Select duration',
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const parsed = parseDurationRange(value, precision);
  const [startYear, setStartYear] = useState(parsed.start.year);
  const [startMonth, setStartMonth] = useState(parsed.start.month);
  const [endYear, setEndYear] = useState(parsed.end.year);
  const [endMonth, setEndMonth] = useState(parsed.end.month);
  const [isPresent, setIsPresent] = useState(parsed.isPresent);

  const yearOptions = useMemo(() => buildYearOptions(50), []);
  const monthChips = MONTH_OPTIONS.map((m) => ({ value: m.value, label: m.label.slice(0, 3) }));

  const openPicker = () => {
    const next = parseDurationRange(value, precision);
    setStartYear(next.start.year);
    setStartMonth(next.start.month);
    setEndYear(next.end.year);
    setEndMonth(next.end.month);
    setIsPresent(next.isPresent);
    setOpen(true);
  };

  const applyDuration = () => {
    const start = { year: startYear, month: startMonth };
    const end = isPresent ? 'Present' : { year: endYear, month: endMonth };
    onChange(formatDurationRange(start, end, precision));
    setOpen(false);
  };

  const triggerClass = `themed-input w-full px-4 py-3 rounded-lg text-left flex items-center justify-between focus:ring-2 focus:ring-[var(--color-ring)] focus:border-transparent ${
    error ? 'border-red-500' : ''
  }`;

  return (
    <>
      <button type="button" onClick={openPicker} className={triggerClass}>
        <span className={value ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}>
          {value || placeholder}
        </span>
        <Calendar className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 ml-2" />
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="elevated-card w-full max-w-lg rounded-xl p-5 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-[var(--color-text)] mb-4">{title}</h3>

            <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">From</p>
            {precision === 'month' ? (
              <ChipRow
                options={monthChips}
                selected={startMonth}
                onSelect={setStartMonth}
              />
            ) : null}
            <ChipRow options={yearOptions} selected={startYear} onSelect={setStartYear} />

            <div className="flex items-center justify-between mt-4 mb-2">
              <p className="text-sm font-medium text-[var(--color-text-secondary)]">To</p>
              {allowPresent ? (
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPresent}
                    onChange={(e) => setIsPresent(e.target.checked)}
                    className="rounded border-[var(--color-border-subtle)]"
                  />
                  Present
                </label>
              ) : null}
            </div>

            {!isPresent ? (
              <>
                {precision === 'month' ? (
                  <ChipRow
                    options={monthChips}
                    selected={endMonth}
                    onSelect={setEndMonth}
                  />
                ) : null}
                <ChipRow options={yearOptions} selected={endYear} onSelect={setEndYear} />
              </>
            ) : (
              <p className="text-sm text-blue-300 mb-3">End date set to Present (current role)</p>
            )}

            <button
              type="button"
              onClick={applyDuration}
              className="brand-gradient-btn w-full mt-4 px-4 py-2.5 rounded-lg font-medium"
            >
              Use this duration
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
